import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import Dropzone from 'react-dropzone';
import PropTypes from 'prop-types';
import { IconButton, Tooltip, CircularProgress } from '@mui/material';
import { Download as DownloadIcon } from '@mui/icons-material';
import './DropZone.css';
import { DEFAULT_OUTPUT_FORMAT, DEFAULT_TEXT_ACCERT } from '../ai/shared/constants/outputFormats';
import { useNotification } from '../../components/index';
import { useTemplates } from './hooks/useTemplates';

const BYTES_IN_KB = 1024;
const BYTES_IN_MB = BYTES_IN_KB * 1024;
const BYTES_IN_GB = BYTES_IN_MB * 1024;
const ABSOLUTE_TOTAL_LIMIT = 1.5 * BYTES_IN_GB; // 1.5 ГБ

// Вспомогательная функция для форматирования размера файла
const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Б';
    if (bytes >= BYTES_IN_GB) {
        return (bytes / BYTES_IN_GB).toFixed(1) + ' ГБ';
    }
    if (bytes >= BYTES_IN_MB) {
        return (bytes / BYTES_IN_MB).toFixed(1) + ' МБ';
    }
    return (bytes / BYTES_IN_KB).toFixed(1) + ' КБ';
};

export const DropZoneBase = ({
    maxFiles = 1,
    maxFileSize = ABSOLUTE_TOTAL_LIMIT,
    files: externalFiles = [],
    onFilesChange,
    isLoading = false,
    name,
    expectedContent,
    accept,
    fileTypesDescription,
    dropzoneMessage,
    maxTotalSize,
    outputFormats = DEFAULT_TEXT_ACCERT,
    defaultOutputFormat = DEFAULT_OUTPUT_FORMAT,
    showFormatSelector = false,
    showTemplateSelector = false,
    className,
    recomendedMaxFileLength,
    audioFormats,
    videoFormats,
    defaultTemplate = null,        
    onFormatChange, 
    onTemplateChange,
}) => {
    const [files, setFiles] = useState(externalFiles);
    const [isDragActive, setIsDragActive] = useState(false);
    const { showError, showInfo } = useNotification();

    // Загружаем шаблоны только если оба селектора включены
    const shouldLoadTemplates = showTemplateSelector && showFormatSelector &&
        files.some(item => item.outputFormat === 'docx');

    const {
        templates,
        loading: templatesLoading,
        error: templatesError,
        downloadTemplate,
        reload: reloadTemplates,
    } = useTemplates({ enabled: shouldLoadTemplates });

    // Рефы для отслеживания изменения количества docx-файлов
    const prevDocxCountRef = useRef(0);
    const isFirstRunRef = useRef(true);

    // Перезагружаем шаблоны при добавлении нового docx-файла (включая смену формата)
    useEffect(() => {
        const currentDocxCount = files.filter(item => item.outputFormat === 'docx').length;
        if (isFirstRunRef.current) {
            isFirstRunRef.current = false;
            prevDocxCountRef.current = currentDocxCount;
            return;
        }
        if (currentDocxCount > prevDocxCountRef.current) {
            reloadTemplates(); // принудительная перезагрузка
        }
        prevDocxCountRef.current = currentDocxCount;
    }, [files, reloadTemplates]);


    // Refs для синхронизации с внешним пропсом files
    const prevFilesRef = useRef(externalFiles);
    const prevExternalFilesRef = useRef(externalFiles);
    const isInitialMount = useRef(true);

    // Синхронизация с внешним пропсом files
    useEffect(() => {
        if (prevExternalFilesRef.current !== externalFiles) {
            prevExternalFilesRef.current = externalFiles;
            setFiles(externalFiles);
        }
    }, [externalFiles]);

    // Уведомляем родителя об изменении внутреннего состояния файлов
    useEffect(() => {
        if (isInitialMount.current) {
            isInitialMount.current = false;
            prevFilesRef.current = files;
            return;
        }

        const prev = prevFilesRef.current;
        prevFilesRef.current = files;

        if (prev === files) return;
        if (JSON.stringify(prev) === JSON.stringify(files)) return;
        if (!onFilesChange || isLoading) return;

        // Если включен хотя бы один селектор – передаём объекты, иначе – только файлы
        if (showFormatSelector || showTemplateSelector) {
            onFilesChange(files);
        } else {
            onFilesChange(files.map((f) => (f.file ? f.file : f)));
        }
    }, [files, onFilesChange, isLoading, showFormatSelector, showTemplateSelector]);

    const getMaxTotalSize = useCallback(() => {
        if (typeof maxTotalSize === 'number') return maxTotalSize;
        return Math.min(maxFiles * maxFileSize, ABSOLUTE_TOTAL_LIMIT);
    }, [maxFiles, maxFileSize, maxTotalSize]);

    const getFilesLimitError = useCallback(
        (withInstruction = false) => {
            const instruction = withInstruction
                ? '. Удалите существующие файлы, чтобы добавить новые.'
                : '';
            const wordForm = maxFiles > 1 ? (maxFiles < 5 ? 'а' : 'ов') : '';
            return `Можно загружать не более ${maxFiles} файл${wordForm}${instruction}`;
        },
        [maxFiles]
    );

    const validateFiles = useCallback(
        (acceptedFiles, rejectedFiles) => {
            if (isLoading) {
                return { isValid: false, error: 'Дождитесь окончания текущей операции' };
            }

            if (rejectedFiles?.length > 0) {
                const hasInvalidType = rejectedFiles.some((file) =>
                    file.errors.some((e) => e.code === 'file-invalid-type')
                );
                const hasTooManyFiles = rejectedFiles.some((file) =>
                    file.errors.some((e) => e.code === 'too-many-files')
                );
                const hasTooLargeFile = rejectedFiles.some((file) =>
                    file.errors.some((e) => e.code === 'file-too-large')
                );

                if (hasTooLargeFile) {
                    return {
                        isValid: false,
                        error: `Размер каждого файла не должен превышать ${maxFileSize / BYTES_IN_GB} ГБ`,
                    };
                }

                return {
                    isValid: false,
                    error: hasInvalidType
                        ? `Можно загружать только ${fileTypesDescription}`
                        : hasTooManyFiles
                            ? getFilesLimitError()
                            : null,
                };
            }

            const existingFileNames = files.map((item) =>
                item.file ? item.file.name : item.name
            );

            const duplicateFiles = acceptedFiles.filter((file) =>
                existingFileNames.includes(file.name)
            );

            if (duplicateFiles.length > 0) {
                return {
                    isValid: false,
                    error: `Файл "${duplicateFiles[0].name}" уже добавлен`,
                };
            }

            const remainingSlots = maxFiles - files.length;
            if (remainingSlots <= 0) {
                return { isValid: false, error: getFilesLimitError(true) };
            }

            const filesToAdd = acceptedFiles.slice(0, remainingSlots);

            const newFiles = [
                ...files,
                ...filesToAdd.map((file) => {
                    let item = showFormatSelector
                        ? { file, outputFormat: defaultOutputFormat }
                        : file;
                    if (showTemplateSelector) {
                        // Добавляем поле templateName (будет использоваться только для docx)
                        item = { ...item, templateName: null };
                    }
                    return item;
                }),
            ];

            const totalSize = newFiles.reduce(
                (sum, item) => sum + ((item.file || item).size || 0),
                0
            );
            const maxTotal = getMaxTotalSize();

            if (totalSize > maxTotal) {
                return {
                    isValid: false,
                    error: `Общий размер файлов не должен превышать ${formatFileSize(maxTotal)}. Сейчас: ${formatFileSize(totalSize)}`,
                };
            }

            return {
                isValid: true,
                filesToAdd,
                acceptedFilesCount: acceptedFiles.length,
                remainingSlots,
            };
        },
        [
            isLoading,
            files,
            maxFiles,
            maxFileSize,
            fileTypesDescription,
            showFormatSelector,
            showTemplateSelector,
            defaultOutputFormat,
            getFilesLimitError,
            getMaxTotalSize,
        ]
    );

    const onDrop = useCallback(
        (acceptedFiles, rejectedFiles) => {
            if (isLoading) {
                showError('Нельзя добавлять файлы во время загрузки');
                return;
            }

            setIsDragActive(false);

            const validation = validateFiles(acceptedFiles, rejectedFiles);
            if (!validation.isValid) {
                showError(validation.error);
                return;
            }

            const { filesToAdd, acceptedFilesCount, remainingSlots } = validation;

            setFiles((prev) => {
                const newItems = filesToAdd.map((file) => {
                    let item = showFormatSelector
                        ? { file, outputFormat: defaultOutputFormat }
                        : file;
                    if (showTemplateSelector) {
                        const template = (defaultOutputFormat === 'docx') ? defaultTemplate : null;
                        item = { ...item, templateName: template };
                    }
                    return item;
                });
                return [...prev, ...newItems];
            });

            if (acceptedFilesCount > remainingSlots) {
                const wordForm =
                    remainingSlots > 1 ? (remainingSlots < 5 ? 'а' : 'ов') : '';
                const maxWordForm =
                    maxFiles > 1 ? (maxFiles < 5 ? 'а' : 'ов') : '';
                showInfo(`Добавлено ${remainingSlots} файл${wordForm} из ${acceptedFilesCount}. Лимит - ${maxFiles} файл${maxWordForm}.`);
            }
        },
        [
            isLoading,
            validateFiles,
            showInfo,
            showFormatSelector,
            showTemplateSelector,
            defaultTemplate,
            defaultOutputFormat,
            maxFiles,
        ]
    );

    const removeFile = useCallback(
        (index) => {
            if (isLoading) {
                showError('Нельзя удалять файлы во время загрузки');
                return;
            }
            setFiles((prev) => prev.filter((_, i) => i !== index));
        },
        [isLoading, showError]
    );

    const handleFormatChange = useCallback((index, format) => {
        if (isLoading) return;
        setFiles((prev) =>
            prev.map((item, i) => {
                if (i !== index) return item;
                const updated = { ...item, outputFormat: format };
                if (format !== 'docx') {
                    delete updated.templateName;
                } else {
                    if (updated.templateName === undefined || updated.templateName === null) {
                        updated.templateName = defaultTemplate || '';
                    }
                }
                return updated;
            })
        );
        // Уведомляем родителя об изменении формата
        if (onFormatChange) {
            onFormatChange(format);
        }
    }, [isLoading, defaultTemplate, onFormatChange]);

    const handleTemplateChange = useCallback((index, templateName) => {
        if (isLoading) return;
        setFiles((prev) =>
            prev.map((item, i) =>
                i === index ? { ...item, templateName } : item
            )
        );
        if (onTemplateChange) {
            onTemplateChange(templateName);
        }
    }, [isLoading, onTemplateChange]);

    useEffect(() => {
        if (!templates || templates.length === 0 || !showTemplateSelector) return;
        let needUpdate = false;
        const updated = files.map(item => {
            if (item.templateName && !templates.includes(item.templateName)) {
                needUpdate = true;
                return { ...item, templateName: null };
            }
            return item;
        });
        if (needUpdate) {
            setFiles(updated);
        }
    }, [templates, showTemplateSelector]);

    const clearAllFiles = useCallback(() => {
        if (isLoading) {
            showError('Нельзя очищать файлы во время загрузки');
            return;
        }
        setFiles([]);
    }, [isLoading, showError]);

    const onDragEnter = useCallback(() => setIsDragActive(true), []);
    const onDragLeave = useCallback(() => setIsDragActive(false), []);

    const maxTotalSizeFormatted = useMemo(
        () => (getMaxTotalSize() / BYTES_IN_GB).toFixed(1),
        [getMaxTotalSize]
    );
    const maxFileSizeGB = useMemo(() => formatFileSize(maxFileSize), [maxFileSize]);

    const renderDropzoneContent = useCallback(
        ({ isDragActive: dragActive }) => {
            if (isLoading) {
                return (
                    <>
                        <p className="dropzone-message loading">Загрузка файлов...</p>
                        <div className="dropzone-spinner">
                            <div className="spinner-border text-primary" role="status">
                                <span className="sr-only">Загрузка...</span>
                            </div>
                        </div>
                    </>
                );
            }

            if (dragActive) {
                return <p className="dropzone-message active">Отпустите файл для загрузки</p>;
            }

            return (
                <>
                    <p className="dropzone-message">{dropzoneMessage}</p>
                    <div className="dropzone-info">
                        {expectedContent && (
                            <>
                                <p>
                                    Рекомендованное содержание файла:{' '}
                                    <em>{expectedContent}</em>
                                </p>
                                <br />
                            </>
                        )}
                        {className === "audio" ? (
                            <>
                                <p>
                                    Аудио:{' '}
                                    <strong>{audioFormats}</strong>
                                    {' · '}Видео:{' '}
                                    <strong>{videoFormats}</strong> (извлечём звук)
                                </p>
                            </>
                        ) : (
                            <p>
                                {fileTypesDescription.includes(",") ? 'Форматы' : 'Формат'}:{' '}
                                <strong>{fileTypesDescription}</strong>
                            </p>
                        )}
                        <br />
                        <p>
                            <strong>До {maxFiles} {maxFiles === 1 ? "файла" : "файлов"} суммарно не более {maxFileSizeGB}.</strong>
                        </p>
                        <p>
                            <strong>{recomendedMaxFileLength}</strong>
                        </p>
                        <p>
                            <strong>Загружая файлы, Вы подтверждаете наличие согласия участников на обработку записи.</strong>
                        </p>

                    </div>
                </>
            );
        },
        [
            isLoading,
            dropzoneMessage,
            expectedContent,
            fileTypesDescription,
            maxFiles,
            maxFileSizeGB,
            maxTotalSizeFormatted,
        ]
    );

    const renderFileList = useCallback(() => {
        const filesCount = files.length;

        return (
            <div className="upload-file-list-container">
                <div className="file-list-header">
                    <p>
                        Загруженные файлы: <span className="files-counter">{filesCount}/{maxFiles}</span>
                    </p>
                    <button
                        className={`default-button ${!isLoading ? 'active-dt1520-button' : 'disabled'} dt1520-button clear-all-btn dropzone-btn`}
                        onClick={clearAllFiles}
                        aria-label="Очистить все файлы"
                        type="button"
                        draggable="false"
                        disabled={isLoading}
                    >
                        Очистить все
                    </button>
                </div>
                <ul className="file-list">
                    {files.map((item, index) => {
                        const file = item.file || item;
                        const { name: fileName, size: fileSize } = file;
                        const outputFormat = item.outputFormat;

                        return (
                            <li
                                key={`${fileName}-${index}`}
                                className={isLoading ? 'file-item-loading' : ''}
                            >
                                <div className="file-info">
                                    <span className="file-name">{fileName}</span>
                                    <span className="file-size">
                                        ({formatFileSize(fileSize)})
                                    </span>
                                </div>

                                {showFormatSelector && outputFormat !== undefined && (
                                    <div className="standard-selector-container">
                                        <label htmlFor={`format-${index}`}>
                                            Формат результата:
                                        </label>
                                        <select
                                            id={`format-${index}`}
                                            value={outputFormat}
                                            onChange={(e) =>
                                                handleFormatChange(index, e.target.value)
                                            }
                                            disabled={isLoading}
                                            className="standard-select"
                                        >
                                            {outputFormats.map((format) => (
                                                <option key={format.value} value={format.value}>
                                                    {format.label}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                )}

                                {/* Блок выбора шаблона – показываем только если:
                                    - showTemplateSelector и showFormatSelector включены
                                    - текущий outputFormat === 'docx'
                                */}
                                {showTemplateSelector &&
                                    showFormatSelector &&
                                    outputFormat === 'docx' && (
                                        <div className="standard-selector-container">
                                            <label htmlFor={`template-${index}`}>Шаблон:</label>
                                            {templatesLoading ? (
                                                <CircularProgress color="primary" size={20} title="Загрузка шаблонов..." />
                                            ) : templatesError ? (
                                                <span className="standard-text">Ошибка загрузки</span>
                                            ) : (
                                                <>
                                                    <select
                                                        id={`template-${index}`}
                                                        value={item.templateName || ''}
                                                        onChange={(e) =>
                                                            handleTemplateChange(index, e.target.value)
                                                        }
                                                        disabled={isLoading || templatesLoading}
                                                        className="standard-select"
                                                    >
                                                        <option value="">Не выбран</option>
                                                        {Array.isArray(templates) &&
                                                            templates.map((tmpl) => (
                                                                <option key={tmpl} value={tmpl}>
                                                                    {tmpl}
                                                                </option>
                                                            ))}
                                                    </select>
                                                    {item.templateName && (
                                                        <Tooltip title="Скачать шаблон">
                                                            <IconButton
                                                                className="standard-icon-button"
                                                                onClick={() => downloadTemplate(item.templateName)}
                                                                disabled={isLoading}
                                                                size="small"
                                                            >
                                                                <DownloadIcon className="blue-color" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}
                                                </>
                                            )}
                                        </div>
                                    )}

                                <button
                                    className="file-remove"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        removeFile(index);
                                    }}
                                    disabled={isLoading}
                                    aria-label="Удалить файл"
                                    type="button"
                                    draggable="false"
                                >
                                    <span className="close-icon">&times;</span>
                                </button>
                            </li>
                        );
                    })}
                </ul>
            </div>
        );
    }, [
        files,
        maxFiles,
        isLoading,
        clearAllFiles,
        showFormatSelector,
        showTemplateSelector,
        outputFormats,
        handleFormatChange,
        handleTemplateChange,
        removeFile,
        templates,
        templatesLoading,
        templatesError,
        downloadTemplate,
    ]);

    return (
        <section className={`${className}-dropzone ${isLoading ? 'loading' : ''}`}>
            <Dropzone
                accept={accept}
                onDrop={onDrop}
                maxFiles={maxFiles}
                onDragEnter={onDragEnter}
                onDragLeave={onDragLeave}
                disabled={isLoading}
            >
                {({ getRootProps, getInputProps }) => (
                    <div
                        {...getRootProps()}
                        className={`dropzone-container outline-disabled ${isDragActive ? 'active' : ''} ${isLoading ? 'disabled' : ''}`}
                    >
                        <input
                            {...getInputProps({
                                name,
                                multiple: maxFiles > 1,
                                disabled: isLoading,
                            })}
                        />
                        <div className="dropzone-content">
                            {renderDropzoneContent({ isDragActive })}
                            <div className="dropzone-icon">
                                <i className="icon-upload"></i>
                            </div>
                        </div>
                    </div>
                )}
            </Dropzone>

            {files.length > 0 && renderFileList()}
        </section>
    );
};

DropZoneBase.propTypes = {
    maxFiles: PropTypes.number,
    onFilesChange: PropTypes.func,
    isLoading: PropTypes.bool,
    files: PropTypes.array,
    name: PropTypes.string,
    expectedContent: PropTypes.string,
    accept: PropTypes.object.isRequired,
    fileTypesDescription: PropTypes.string.isRequired,
    dropzoneMessage: PropTypes.string.isRequired,
    maxTotalSize: PropTypes.number,
    outputFormats: PropTypes.arrayOf(
        PropTypes.shape({
            value: PropTypes.string.isRequired,
            label: PropTypes.string.isRequired,
        })
    ),
    defaultOutputFormat: PropTypes.string,
    showFormatSelector: PropTypes.bool,
    showTemplateSelector: PropTypes.bool,
    maxFileSize: PropTypes.number,
    className: PropTypes.string,
    recomendedMaxFileLength: PropTypes.string,
    audioFormats: PropTypes.string,
    videoFormats: PropTypes.string,
};

export default DropZoneBase;